import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Home, PlusCircle, ClipboardList, LogOut, Store,
} from "lucide-react";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { confirmLeave } from "@/lib/unsaved";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect, useRef } from "react";
import { BackupAlert } from "@/components/BackupAlert";

const ROLE_LABELS = {
  employee: "Employee",
  store_manager: "Manager",
  accountant: "Accountant",
  owner: "Owner",
};

export default function Layout({ children }) {
  const { user, storeOptions, selectedStore, setSelectedStore, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showNew, setShowNew] = useState(false);

  const navItems = [
    { path: "/dashboard", icon: Home, label: "Home" },
    { path: "/record-purchase", icon: PlusCircle, label: "Record" },
    { path: "/submissions", icon: ClipboardList, label: "History" },
  ];

  const isActive = (path) => location.pathname === path;
  const isRecordActive = isActive("/record-purchase") || isActive("/record-expense");
  const scrollRef = useRef(null);
  useEffect(() => { scrollRef.current?.scrollTo(0, 0); }, [location.pathname]);

  return (
    <div className="h-screen h-[100dvh] flex flex-col overflow-hidden bg-cream" data-testid="app-shell">
      <header className="gm-header shrink-0 z-50">
        <div className="flex items-center justify-between px-3 sm:px-4 h-14 max-w-4xl mx-auto">
          <img src="/grainmill-logo.jpg" alt="Grainmill" className="h-9 cursor-pointer rounded"
            onClick={() => { if (confirmLeave()) navigate("/dashboard"); }} />

          {storeOptions.length > 0 && (
            <Select
              value={String(selectedStore?.id || "")}
              onValueChange={(val) => {
                const s = storeOptions.find((x) => String(x.id) === val);
                if (s) setSelectedStore(s);
              }}
            >
              <SelectTrigger className="w-[140px] bg-navy-light/50 border-gold/20 text-cream text-xs min-h-[44px] h-11 rounded-lg"
                data-testid="store-selector-dropdown">
                <Store className="w-3.5 h-3.5 mr-1.5 text-gold" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {storeOptions.map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1.5 outline-none min-w-[44px] min-h-[44px] justify-center" data-testid="user-menu-trigger" aria-label="Account menu">
              <Avatar className="h-9 w-9 bg-gold/20 border border-gold/30">
                <AvatarFallback className="bg-gold/20 text-gold text-xs font-bold">
                  {user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <div className="px-3 py-2.5 border-b">
                <p className="text-sm font-semibold">{user?.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{user?.email}</p>
                <Badge variant="outline" className="mt-1.5 text-[10px] border-gold/30 text-gold"
                  data-testid="role-switcher-badge">
                  {ROLE_LABELS[user?.role] || user?.role}
                </Badge>
              </div>
              <DropdownMenuItem onClick={async () => { if (!confirmLeave()) return; await logout(); navigate("/login"); }} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain" data-testid="main-scroll">
        <BackupAlert />
        <main className="px-4 pt-5 pb-8 max-w-2xl mx-auto" data-testid="main-content">{children}</main>
      </div>

      <nav className="gm-bottom-nav shrink-0 z-50" data-testid="bottom-nav">
        {showNew && (
          <div className="bg-navy border-t border-gold/20 p-3 animate-in slide-in-from-bottom-4 duration-200">
            <div className="max-w-2xl mx-auto flex gap-3">
              <button onClick={() => { setShowNew(false); if (confirmLeave()) navigate("/record-purchase"); }}
                className="flex-1 gm-btn-gold text-sm" data-testid="new-purchase-btn">Purchase</button>
              <button onClick={() => { setShowNew(false); if (confirmLeave()) navigate("/record-expense"); }}
                className="flex-1 gm-btn-gold text-sm" data-testid="new-expense-btn">Expense</button>
            </div>
          </div>
        )}
        <div className="flex justify-around items-center h-16 max-w-2xl mx-auto">
          {navItems.map((item) => {
            const active = item.path === "/record-purchase" ? isRecordActive : isActive(item.path);
            return (
              <button key={item.path}
                onClick={() => { if (item.path === "/record-purchase") { setShowNew((p) => !p); } else { setShowNew(false); if (confirmLeave()) navigate(item.path); } }}
                className={`flex flex-col items-center gap-0.5 py-1.5 px-5 rounded-lg transition-colors ${active ? "text-gold" : "text-cream/40 hover:text-cream/70"}`}
                data-testid={`nav-${item.label.toLowerCase()}`}>
                <item.icon className="w-5 h-5" />
                <span className="text-[10px] font-medium tracking-wide">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
